import type { ThemeConfig } from 'antd';

/**
 * Ant Design Theme Configuration
 *
 * IMPORTANT: These values should be kept in sync with CSS variables in index.css.
 * Ant Design doesn't natively support CSS variables in token config, so we keep
 * matching values here and document the correspondence.
 *
 * CSS Variable Mapping:
 * - colorBgBase/colorBgContainer → --rp-surface-primary (#ffffff)
 * - colorBgElevated → --rp-surface-tertiary (#fafbfc)
 * - colorText → --rp-gray-900 (#2c3e50)
 * - colorTextSecondary → --rp-gray-700 (#5e7492)
 * - colorTextTertiary → --rp-gray-500 (#8fa4b3)
 * - colorBorder → --rp-border-standard (#d1d9e6)
 *
 * When updating colors, update BOTH this file AND index.css to maintain consistency.
 */
const theme: ThemeConfig = {
  token: {
    // Typography - System fonts
    fontFamily: `
      -apple-system,
      BlinkMacSystemFont,
      "SF Pro Display",
      "Segoe UI",
      Roboto,
      "Helvetica Neue",
      Arial,
      sans-serif
    `,

    // Brand Color Scheme (harmonized with marketing palette)
    colorPrimary: '#1890ff',   // Rocket Blue - primary brand (--rp-blue-primary)
    colorSuccess: '#13c2c2',   // Cyan-green - harmonized with blue palette
    colorWarning: '#fa8c16',   // Orange - split-complementary accent
    colorError: '#ff4d4f',     // Red - true errors only
    colorInfo: '#597ef7',      // Blue-purple - distinct from primary

    // Background colors (sync with --rp-surface-* tokens)
    colorBgBase: '#ffffff',      // --rp-surface-primary
    colorBgContainer: '#ffffff', // --rp-surface-primary
    colorBgElevated: '#f7f8fa',  // --rp-surface-tertiary

    // Text colors (sync with --rp-gray-* tokens)
    colorText: '#2c3e50',          // --rp-gray-900
    colorTextSecondary: '#5e7492', // --rp-gray-700
    colorTextTertiary: '#8fa4b3',  // --rp-gray-500

    // Border and layout (sync with --rp-border-* tokens)
    colorBorder: '#c4cdd9', // --rp-border-standard
    borderRadius: 6, // Matches header button radius for consistency
    
    // Component specific
    controlHeight: 40,
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    
    // Spacing
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    
    // Subtle brand-tinted shadows (6-8% opacity for data-heavy screens)
    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    boxShadowSecondary: '0 4px 12px rgba(24, 144, 255, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)',
  },
  
  // Component specific overrides
  components: {
    Button: {
      colorPrimary: '#1890ff',
      algorithm: true, // Use algorithm to generate color variants
      controlHeight: 40,
      borderRadius: 6, // Matches header button radius
      fontWeight: 500,
    },
    Card: {
      colorBorder: '#d1d9e6',
      colorBorderSecondary: '#b8c5d6',
      borderRadiusLG: 8, // Reduced from 12 for tighter consistency with buttons
      paddingLG: 24,
      // Subtle shadows for data-heavy screens (reduced from 25-30% to 6-8%)
      boxShadow: '0 2px 8px rgba(24, 144, 255, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
      boxShadowTertiary: '0 4px 12px rgba(24, 144, 255, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)',
    },
    Input: {
      colorBorder: '#d1d9e6',
      borderRadius: 6, // Matches button radius
      controlHeight: 40,
    },
    Layout: {
      // Header with subtle gradient for premium feel (Inara recommendation)
      headerBg: 'linear-gradient(135deg, #1890ff 0%, #0e7ae0 100%)',
      headerHeight: 64,      // Match marketing site's h-16 (64px) header height
      headerPadding: '0 24px', // Horizontal padding only
      bodyBg: '#f5f8ff',     // Light blue-gray background
      triggerBg: '#096dd9',  // Rocket Blue Dark for sider trigger
    },
    Menu: {
      itemBg: 'transparent',
      itemColor: '#5e7492',           // --rp-gray-700 - secondary text
      itemHoverColor: '#2c3e50',      // --rp-gray-900 - stronger on hover
      itemHoverBg: 'rgba(0, 0, 0, 0.04)', // --rp-surface-hover
      itemSelectedColor: '#1890ff',   // Rocket Blue
      itemSelectedBg: 'rgba(24, 144, 255, 0.08)', // --rp-surface-selected
      itemActiveBg: '#bae7ff',        // Rocket Blue Muted - keyboard focus
    },
    Typography: {
      colorTextHeading: '#1e3a5f', // Dark blue for headings
      fontWeightStrong: 600,
    },

    // Drawer - Enhanced visual design for record editing
    Drawer: {
      colorBgElevated: '#ffffff',
      paddingLG: 24,
      // Header with subtle background
      colorBgMask: 'rgba(0, 0, 0, 0.45)',
    },

    // Collapse - Card-like panels for form sections
    // Note: Panel backgrounds use --rp-surface-tertiary-gradient via inline styles
    Collapse: {
      // Panel styling (--rp-surface-tertiary)
      colorBgContainer: '#f7f8fa',
      colorBorder: '#c4cdd9', // --rp-border-standard (for visible panel edges)
      borderRadiusLG: 8,
      // Header (using slightly darker for hierarchy)
      headerBg: '#f5f7fa', // --rp-surface-secondary
      colorTextHeading: '#1e3a5f',
      fontSizeLG: 14,
      // Content
      padding: 16,
      paddingLG: 16,
    },

    // Alert - Enhanced banners
    Alert: {
      borderRadiusLG: 8,
    },

    // Tag - Lifecycle state badges
    Tag: {
      borderRadiusSM: 4,
      fontSizeSM: 13,
    },
  },
  
  // Algorithm for color generation
  algorithm: [],
};

export default theme;